// src/pages/admin/AdminGuiaSupervision.js
//
// Vista read-only para que el SUPERADMIN supervise a un guía específico.
// Muestra lo mismo que el guía ve en /guia/*, pero filtrado por guiaId
// y sin permitir cambios. Los cambios se hacen desde /admin/guias
// (edición, cambio de estado, etc.).

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Card, CardContent, Stack, Box, Grid,
  Tabs, Tab, Table, TableHead, TableBody, TableRow, TableCell,
  LinearProgress, Alert, Chip, Button, Avatar, Divider, TableContainer,
} from '@mui/material';
import ArrowBackIcon      from '@mui/icons-material/ArrowBack';
import PersonIcon         from '@mui/icons-material/Person';
import VisibilityIcon     from '@mui/icons-material/Visibility';
import { guias as api } from '../../api/guiasApi';
import { useApp } from '../../context/AppContext';
import { fechaLocal } from '../../utils/format';

const fmtCOP = (n) => `$${(n || 0).toLocaleString('es-CO')}`;
const fmtFecha = (iso) => iso ? fechaLocal(iso) : '—';

function KPI({ label, valor, color }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h5" fontWeight={700} color={color || 'text.primary'}>
          {valor}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function AdminGuiaSupervision() {
  const { id } = useParams();
  const guiaId = Number(id);
  const navigate = useNavigate();
  const { notificar } = useApp();

  const [tab, setTab]         = useState(0);
  const [guia, setGuia]       = useState(null);
  const [panel, setPanel]     = useState(null);
  const [afiliados, setAfil]  = useState([]);
  const [comisiones, setCom]  = useState(null);
  const [liqs, setLiqs]       = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    setCargando(true);
    Promise.all([
      api.obtener(guiaId).then(r => setGuia(r.guia)),
      api.supervision.panel(guiaId).then(setPanel),
      api.supervision.afiliados(guiaId).then(setAfil),
      api.supervision.comisiones(guiaId).then(setCom),
      api.supervision.liquidaciones(guiaId).then(setLiqs),
    ])
      .catch(e => notificar(e.mensajeUsuario || 'Error cargando supervisión.', 'error'))
      .finally(() => setCargando(false));
  }, [guiaId, notificar]);

  if (cargando) return <Container sx={{ py: 3 }}><LinearProgress /></Container>;
  if (!guia)    return <Container sx={{ py: 3 }}><Alert severity="warning">Guía no encontrado.</Alert></Container>;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/guias')}>
          Volver a la lista
        </Button>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              src={guia.fotoUrl?.startsWith('gs://') ? undefined : guia.fotoUrl}
              sx={{ width: 64, height: 64, bgcolor: 'grey.200' }}
            >
              {!guia.fotoUrl && <PersonIcon sx={{ fontSize: 36, color: 'grey.500' }} />}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h5" fontFamily="Fraunces">{guia.nombreCompleto}</Typography>
                <Chip label={guia.estado} size="small"
                      color={guia.estado === 'ACTIVO' ? 'success' : 'warning'} />
                <Chip icon={<VisibilityIcon fontSize="small" />}
                      label="Supervisando (solo lectura)"
                      size="small" variant="outlined" color="info" />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {guia.tipoDocumento} {guia.numeroDocumento} · {guia.telefono} · {guia.correo}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Panel" />
        <Tab label={`Afiliados (${afiliados.length})`} />
        <Tab label="Comisiones" />
        <Tab label={`Liquidaciones (${liqs.length})`} />
      </Tabs>

      {tab === 0 && panel && (
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <KPI label="Afiliados activos" valor={panel.afiliadosActivos} />
            </Grid>
            <Grid item xs={6} md={3}>
              <KPI label="Con HolaVeci 360°" valor={panel.afiliados360} />
            </Grid>
            <Grid item xs={6} md={3}>
              <KPI label="Total afiliados histórico" valor={panel.totalAfiliados} />
            </Grid>
            <Grid item xs={6} md={3}>
              <KPI label="% Comisión" valor={`${panel.porcentajeComision}%`} />
            </Grid>
            <Grid item xs={12} md={3}>
              <KPI label="Comisión acumulada" valor={fmtCOP(panel.comisionAcumulada)} color="primary.main" />
            </Grid>
            <Grid item xs={12} md={3}>
              <KPI label="Comisión mes actual" valor={fmtCOP(panel.comisionMesActual)} color="primary.main" />
            </Grid>
            <Grid item xs={12} md={3}>
              <KPI label="Pagado en liquidaciones" valor={fmtCOP(panel.totalPagado)} color="success.main" />
            </Grid>
            <Grid item xs={12} md={3}>
              <KPI label="Pendiente por pagar" valor={fmtCOP(panel.totalPendiente)} color="warning.main" />
            </Grid>
          </Grid>
        </Box>
      )}

      {tab === 1 && (
        <TableContainer component={Card} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Razón social</TableCell>
                <TableCell>Nombre comercial</TableCell>
                <TableCell>Productos</TableCell>
                <TableCell>Afiliado desde</TableCell>
                <TableCell>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {afiliados.length === 0 && (
                <TableRow><TableCell colSpan={5} align="center">
                  <Typography color="text.secondary" py={2}>Sin afiliados aún.</Typography>
                </TableCell></TableRow>
              )}
              {afiliados.map(a => (
                <TableRow key={a.empresaId}>
                  <TableCell>{a.razonSocial}</TableCell>
                  <TableCell>{a.nombreComercial || '—'}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {a.contrataHolaVeci && <Chip label="HolaVeci" size="small" color="primary" />}
                      {a.contrataPOS      && <Chip label="POS"      size="small" color="secondary" />}
                    </Stack>
                  </TableCell>
                  <TableCell>{fmtFecha(a.fechaAfiliacionGuia)}</TableCell>
                  <TableCell>
                    <Chip label={a.activa ? 'Activa' : 'Inactiva'} size="small"
                          color={a.activa ? 'success' : 'default'} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 2 && comisiones && (
        <Box>
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <Typography variant="body2" color="text.secondary">
              Total: <b>{comisiones.totalRegistros}</b> facturas ·
              Comisión acumulada: <b>{fmtCOP(comisiones.totalComision)}</b> ·
              % del guía: <b>{comisiones.porcentajeComision}%</b>
            </Typography>
          </Stack>
          <TableContainer component={Card} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Período</TableCell>
                  <TableCell>Factura</TableCell>
                  <TableCell>Empresa</TableCell>
                  <TableCell>Producto</TableCell>
                  <TableCell align="right">Base gravable</TableCell>
                  <TableCell align="right">Comisión</TableCell>
                  <TableCell>Pagada</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comisiones.comisiones.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" py={2}>Sin comisiones aún.</Typography>
                  </TableCell></TableRow>
                )}
                {comisiones.comisiones.map(c => (
                  <TableRow key={c.facturaId}>
                    <TableCell>{c.periodoAnio}-{String(c.periodoMes).padStart(2, '0')}</TableCell>
                    <TableCell>{c.numeroFactura || '—'}</TableCell>
                    <TableCell>{c.nombreComercial || c.empresa}</TableCell>
                    <TableCell>{c.producto}</TableCell>
                    <TableCell align="right">{fmtCOP(c.baseGravable)}</TableCell>
                    <TableCell align="right"><b>{fmtCOP(c.comision)}</b></TableCell>
                    <TableCell>{fmtFecha(c.fechaPago)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tab === 3 && (
        <TableContainer component={Card} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Período</TableCell>
                <TableCell align="right">Comisiones</TableCell>
                <TableCell align="right">Bonos</TableCell>
                <TableCell align="right">Bruto</TableCell>
                <TableCell align="right">Retefuente</TableCell>
                <TableCell align="right">Neto</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Pagada</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {liqs.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" py={2}>Sin liquidaciones aún.</Typography>
                </TableCell></TableRow>
              )}
              {liqs.map(l => (
                <TableRow key={l.liquidacionId}>
                  <TableCell>{fmtFecha(l.periodoDesde)} → {fmtFecha(l.periodoHasta)}</TableCell>
                  <TableCell align="right">{fmtCOP(l.subtotalComisiones)}</TableCell>
                  <TableCell align="right">{fmtCOP(l.subtotalBonos)}</TableCell>
                  <TableCell align="right">{fmtCOP(l.bruto)}</TableCell>
                  <TableCell align="right">{fmtCOP(l.montoRetefuente)}</TableCell>
                  <TableCell align="right"><b>{fmtCOP(l.neto)}</b></TableCell>
                  <TableCell>
                    <Chip label={l.estado} size="small"
                          color={l.estado === 'PAGADA' ? 'success'
                              : l.estado === 'APROBADA' ? 'info'
                              : l.estado === 'CALCULADA' ? 'warning' : 'default'} />
                  </TableCell>
                  <TableCell>{fmtFecha(l.fechaPago)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}
